import React, { Fragment, useEffect, useMemo, useState } from "react";
import { Dialog, Transition } from "@headlessui/react";
import { gps, parse, thumbnailUrl } from "exifr";
import { useTranslation } from "react-i18next";
import ButtonClose from "shared/ButtonClose/ButtonClose";
import ButtonPrimary from "shared/Button/ButtonPrimary";
import ButtonSecondary from "shared/Button/ButtonSecondary";
import { voyageService } from "_services/voyage.service";
import { JourneeType } from "data/types";
import { ChevronUpIcon, InformationCircleIcon } from "@heroicons/react/24/outline";

type TripEvent = JourneeType["evenements"][number];

interface Stage {
  key: string;
  type: "lieu" | "nuit";
  event: TripEvent;
  dayId: number;
  dayNumber: number;
  date: string;
  label: string;
}

interface PendingPhoto {
  id: string;
  originalFile: File;
  uploadFile: File;
  previewUrl: string;
  takenAt: Date | null;
  hasTakenAtMetadata: boolean;
  latitude: number | null;
  longitude: number | null;
  stageKey: string;
  metadataLoading: boolean;
  error?: string;
}

interface Props {
  files: File[];
  idVoyage: string | number;
  eventCategories: JourneeType[];
  onClose: () => void;
  onUploaded: (updates: Array<{ stageKey: string; url: string }>) => void;
}

const MAX_FILE_SIZE = 12 * 1024 * 1024;
const MAX_BATCH_SIZE = 30;

const isHeic = (file: File) => /\.(heic|heif)$/i.test(file.name) || /image\/hei[cf]/i.test(file.type);
const isAllowed = (file: File) => /\.(jpe?g|png|webp|heic|heif)$/i.test(file.name);

const parseDate = (value: unknown): Date | null => {
  if (value instanceof Date && !Number.isNaN(value.getTime())) return value;
  if (typeof value !== "string") return null;
  const parsed = new Date(value.replace(/^(\d{4}):(\d{2}):(\d{2})/, "$1-$2-$3"));
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const normalizeDay = (value: string) => {
  const match = String(value || "").match(/(\d{4})[-/](\d{2})[-/](\d{2})/);
  if (match) return `${match[1]}-${match[2]}-${match[3]}`;
  const frenchMatch = String(value || "").match(/(\d{2})\/(\d{2})\/(\d{4})/);
  return frenchMatch ? `${frenchMatch[3]}-${frenchMatch[2]}-${frenchMatch[1]}` : "";
};

const dateKey = (date: Date | null) => date
  ? `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`
  : "";

const formatStageDate = (value: string, language: string) => {
  const normalizedDate = normalizeDay(value);
  const match = normalizedDate.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!match) return value;
  const date = new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
  return date.toLocaleDateString(language || "fr", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
};

const distanceKm = (lat1: number, lon1: number, lat2: number, lon2: number) => {
  const rad = (value: number) => value * Math.PI / 180;
  const dLat = rad(lat2 - lat1);
  const dLon = rad(lon2 - lon1);
  const a = Math.sin(dLat / 2) ** 2 + Math.cos(rad(lat1)) * Math.cos(rad(lat2)) * Math.sin(dLon / 2) ** 2;
  return 6371 * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
};

const proposeStage = (photo: Pick<PendingPhoto, "takenAt" | "latitude" | "longitude">, stages: Stage[]) => {
  const sameDay = stages.filter(stage => normalizeDay(stage.date) === dateKey(photo.takenAt));
  const candidates = sameDay.length ? sameDay : stages;

  if (photo.latitude !== null && photo.longitude !== null) {
    const located = candidates
      .map(stage => ({
        stage,
        distance: stage.event.lat !== null && stage.event.longi !== null
          ? distanceKm(photo.latitude!, photo.longitude!, Number(stage.event.lat), Number(stage.event.longi))
          : Number.POSITIVE_INFINITY,
      }))
      .filter(item => Number.isFinite(item.distance))
      .sort((a, b) => a.distance - b.distance);

    if (located[0] && (sameDay.length || located[0].distance <= 50)) return located[0].stage.key;
  }

  if (sameDay.length) {
    return sameDay[0].key;
  }

  return "";
};

const ModalBulkPhotoUpload: React.FC<Props> = ({ files, idVoyage, eventCategories, onClose, onUploaded }) => {
  const { t, i18n } = useTranslation();
  const [photos, setPhotos] = useState<PendingPhoto[]>([]);
  const [preparing, setPreparing] = useState(true);
  const [showMobileInfo, setShowMobileInfo] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);

  const stages = useMemo<Stage[]>(() => eventCategories.flatMap(day =>
    day.evenements
      .filter(event => {
        const hasFirstLabel = Boolean(event.libelle1?.trim());
        const hasSecondLabel = Boolean(event.libelle2?.trim());
        return (event.typeEv === "L" && hasFirstLabel) ||
          (event.typeEv === "N" && (hasFirstLabel || hasSecondLabel));
      })
      .map(event => {
        const type = event.typeEv === "N" ? "nuit" : "lieu";
        const details = [event.libelle1, event.libelle2].filter(Boolean).join(" – ");
        return {
          key: `${type}-${event.id}`,
          type,
          event,
          dayId: day.id_journee,
          dayNumber: day.num_jour,
          date: day.date_journee,
          label: `${type === "nuit" ? t("nuit") : t("jour")} ${day.num_jour} · ${formatStageDate(day.date_journee, i18n.language)}${details ? ` · ${details}` : ""}`,
        };
      })
  ), [eventCategories, i18n.language, t]);

  useEffect(() => {
    let cancelled = false;
    const urls: string[] = [];

    const prepare = async () => {
      setPreparing(true);
      const selectedFiles = files.slice(0, MAX_BATCH_SIZE);
      const initial = selectedFiles.map((file, index): PendingPhoto => {
        const previewUrl = URL.createObjectURL(file);
        urls.push(previewUrl);
        return {
          id: `${file.name}-${file.size}-${file.lastModified}-${index}`,
          originalFile: file,
          uploadFile: file,
          previewUrl,
          takenAt: null,
          hasTakenAtMetadata: false,
          latitude: null,
          longitude: null,
          stageKey: "",
          metadataLoading: true,
          error: !isAllowed(file)
            ? t("photoBulk.invalidFormat")
            : file.size > MAX_FILE_SIZE
              ? t("photoBulk.tooLarge")
              : undefined,
        };
      });
      setPhotos(initial);

      for (const item of initial) {
        if (cancelled || item.error) {
          if (!cancelled) setPhotos(current => current.map(photo => photo.id === item.id ? { ...photo, metadataLoading: false } : photo));
          continue;
        }

        let takenAt = new Date(item.originalFile.lastModified);
        let hasTakenAtMetadata = false;
        let latitude: number | null = null;
        let longitude: number | null = null;
        let uploadFile = item.originalFile;
        let previewUrl = item.previewUrl;

        try {
          const metadata: any = await parse(item.originalFile, ["DateTimeOriginal", "CreateDate"]);
          const metadataDate = parseDate(metadata?.DateTimeOriginal || metadata?.CreateDate);
          if (metadataDate) {
            takenAt = metadataDate;
            hasTakenAtMetadata = true;
          }
        } catch (error) {
          console.warn("Unable to read photo metadata", item.originalFile.name, error);
        }

        try {
          const gpsMetadata = await gps(item.originalFile);
          latitude = Number.isFinite(gpsMetadata?.latitude) ? Number(gpsMetadata.latitude) : null;
          longitude = Number.isFinite(gpsMetadata?.longitude) ? Number(gpsMetadata.longitude) : null;
        } catch (error) {
          console.warn("Unable to read photo GPS metadata", item.originalFile.name, error);
        }

        if (isHeic(item.originalFile)) {
          try {
            const { heicTo } = await import("heic-to/csp");
            const blob = await heicTo({ blob: item.originalFile, type: "image/jpeg", quality: 0.82 });
            uploadFile = new File([blob], item.originalFile.name.replace(/\.hei[cf]$/i, ".jpg"), { type: "image/jpeg" });
            previewUrl = URL.createObjectURL(blob);
            urls.push(previewUrl);
          } catch (error) {
            console.warn("Unable to convert HEIC preview", item.originalFile.name, error);
            try {
              const embeddedThumbnail = await thumbnailUrl(item.originalFile);
              if (embeddedThumbnail) {
                previewUrl = embeddedThumbnail;
                urls.push(previewUrl);
              }
            } catch (thumbnailError) {
              console.warn("Unable to extract HEIC thumbnail", item.originalFile.name, thumbnailError);
            }
          }
        }

        if (!cancelled) setPhotos(current => {
          return current.map(photo => photo.id === item.id ? {
            ...photo,
            uploadFile,
            previewUrl,
            takenAt,
            hasTakenAtMetadata,
            latitude,
            longitude,
            stageKey: "",
            metadataLoading: false,
          } : photo);
        });
      }

      if (!cancelled) {
        setPhotos(current => {
          const sortedPhotos = current
          .map((photo, selectionIndex) => ({ photo, selectionIndex }))
          .sort((a, b) => {
            if (a.photo.hasTakenAtMetadata && b.photo.hasTakenAtMetadata) {
              return (a.photo.takenAt?.getTime() || 0) - (b.photo.takenAt?.getTime() || 0);
            }
            if (a.photo.hasTakenAtMetadata !== b.photo.hasTakenAtMetadata) {
              return a.photo.hasTakenAtMetadata ? -1 : 1;
            }
            return a.selectionIndex - b.selectionIndex;
          })
          .map(({ photo }) => photo);

          const usedStages = new Set<string>();
          return sortedPhotos.map(photo => {
            const availableStages = stages.filter(stage => !usedStages.has(stage.key));
            const stageKey = proposeStage(photo, availableStages);
            if (stageKey) usedStages.add(stageKey);
            return { ...photo, stageKey };
          });
        });
        setPreparing(false);
      }
    };

    prepare();
    return () => {
      cancelled = true;
      urls.forEach(URL.revokeObjectURL);
    };
  }, [files, stages, t]);

  const assignedKeys = photos.map(photo => photo.stageKey).filter(Boolean);
  const assignedPhotos = photos.filter(photo => photo.stageKey && !photo.error);
  const metadataPending = photos.some(photo => photo.metadataLoading);

  const setAssociation = (photoId: string, stageKey: string) => {
    setPhotos(current => current.map(photo => photo.id === photoId ? { ...photo, stageKey } : photo));
  };

  const removePhoto = (photoId: string) => {
    setPhotos(current => current.filter(photo => photo.id !== photoId));
  };

  const upload = async () => {
    if (!assignedPhotos.length || uploading) return;
    setUploading(true);
    setProgress(0);
    const updates: Array<{ stageKey: string; url: string }> = [];

    try {
      for (let index = 0; index < assignedPhotos.length; index += 1) {
        const photo = assignedPhotos[index];
        const [type, id] = photo.stageKey.split("-");
        const extension = photo.uploadFile.type === "image/png" ? "png" : photo.uploadFile.type === "image/webp" ? "webp" : "jpg";
        const formData = new FormData();
        formData.append("photo", photo.uploadFile, `photo_${type}_${id}.${extension}`);
        const response = await voyageService.uploadPhoto(idVoyage, formData, { headers: { "Content-Type": "multipart/form-data" } });
        updates.push({ stageKey: photo.stageKey, url: response.data.photoInfo.url });
        setProgress(index + 1);
      }
      onUploaded(updates);
    } catch (error) {
      console.error("Bulk photo upload failed", error);
      alert(t("photoBulk.uploadError"));
      setUploading(false);
    }
  };

  return (
    <Transition appear show as={Fragment}>
      <Dialog as="div" className="fixed inset-0 z-[99999] overflow-y-auto" onClose={uploading ? () => undefined : onClose}>
        <div className="min-h-screen px-4 text-center">
          <Transition.Child as={Fragment} enter="ease-out duration-200" enterFrom="opacity-0" enterTo="opacity-100" leave="ease-in duration-150" leaveFrom="opacity-100" leaveTo="opacity-0">
            <Dialog.Overlay className="fixed inset-0 bg-black/50" />
          </Transition.Child>
          <span className="inline-block h-screen align-middle" aria-hidden="true">&#8203;</span>
          <Transition.Child as={Fragment} enter="ease-out duration-200" enterFrom="opacity-0 scale-95" enterTo="opacity-100 scale-100" leave="ease-in duration-150" leaveFrom="opacity-100 scale-100" leaveTo="opacity-0 scale-95">
            <div className="inline-block w-full max-w-5xl my-8 overflow-hidden text-left align-middle transition-all transform bg-white dark:bg-neutral-900 shadow-xl rounded-2xl">
              <div className="flex items-center justify-between px-6 py-4 border-b border-neutral-200 dark:border-neutral-700">
                <Dialog.Title className="text-xl font-semibold">{t("photoBulk.title")}</Dialog.Title>
                {!uploading && <ButtonClose onClick={onClose} />}
              </div>
              <div className="p-6">
                <p className="mb-5 hidden text-xs text-neutral-600 dark:text-neutral-300 sm:block">{t("photoBulk.intro")}<br/>{t("photoBulk.introSuite")}</p>
                <div className="mb-4 sm:hidden">
                  <button
                    type="button"
                    aria-expanded={showMobileInfo}
                    onClick={() => setShowMobileInfo(value => !value)}
                    className="flex w-full items-center justify-between rounded-xl border border-neutral-200 bg-neutral-50 px-4 py-3 text-left text-sm font-medium dark:border-neutral-700 dark:bg-neutral-800"
                  >
                    <span className="flex items-center gap-2">
                      <InformationCircleIcon className="h-5 w-5 text-[#FF4A52]" />
                      {t("photoBulk.information")}
                    </span>
                    <ChevronUpIcon className={`h-4 w-4 transition-transform ${showMobileInfo ? "" : "rotate-180"}`} />
                  </button>
                  {showMobileInfo && (
                    <div className="mt-2 rounded-lg bg-neutral-50 px-2 py-2 text-xs text-neutral-600 dark:bg-neutral-800 dark:text-neutral-300">
                      <p>{t("photoBulk.intro")}</p>
                      <p className="mt-2">{t("photoBulk.introSuite")}</p>
                    </div>
                  )}
                </div>
                {files.length > MAX_BATCH_SIZE && <p className="mb-4 text-sm couleurRose">{t("photoBulk.batchLimit", { count: MAX_BATCH_SIZE })}</p>}
                {preparing ? (
                  <div className="flex flex-col items-center justify-center min-h-[240px] text-center">
                    <svg className="h-10 w-10" style={{ color: "#FF4A52" }} viewBox="0 0 40 40" aria-hidden="true">
                      <circle cx="20" cy="20" r="16" fill="none" stroke="currentColor" strokeOpacity="0.2" strokeWidth="4" />
                      <path d="M20 4a16 16 0 0 1 16 16" fill="none" stroke="currentColor" strokeWidth="4" strokeLinecap="round">
                        <animateTransform attributeName="transform" type="rotate" from="0 20 20" to="360 20 20" dur="0.8s" repeatCount="indefinite" />
                      </path>
                    </svg>
                    <p className="mt-4 font-medium">{t("photoBulk.preparing")}</p>
                    <p className="mt-1 text-sm text-neutral-500">{t("photoBulk.preparingHint")}</p>
                  </div>
                ) : (
                <div className="space-y-4 max-h-[60vh] overflow-y-auto pr-1">
                  {photos.map(photo => {
                    const selectedStage = stages.find(stage => stage.key === photo.stageKey);
                    return (
                      <div key={photo.id} className="grid grid-cols-1 sm:grid-cols-[140px_1fr_auto] gap-4 p-3 border border-neutral-200 dark:border-neutral-700 rounded-xl">
                        <img src={photo.previewUrl} alt={photo.originalFile.name} className="w-full sm:w-[140px] h-28 object-cover rounded-lg bg-neutral-100" />
                        <div className="min-w-0">
                          <div className="font-medium truncate">{photo.originalFile.name}</div>
                          {photo.metadataLoading ? <div className="text-xs mt-1">{t("photoBulk.readingMetadata")}</div> : (
                            <div className="text-xs mt-1 text-neutral-500">
                              {photo.takenAt ? photo.takenAt.toLocaleString() : t("photoBulk.noDate")}
                              {photo.latitude !== null && photo.longitude !== null ? ` · GPS ${photo.latitude.toFixed(4)}, ${photo.longitude.toFixed(4)}` : ` · ${t("photoBulk.noGps")}`}
                            </div>
                          )}
                          {photo.error ? <div className="text-sm mt-3 text-red-600">{photo.error}</div> : (
                            <>
                              <label className="block text-sm font-medium mt-3 mb-1">{t("photoBulk.association")}</label>
                              <select className="w-full rounded-lg border-neutral-300 dark:bg-neutral-800 text-xs" value={photo.stageKey} onChange={event => setAssociation(photo.id, event.target.value)} disabled={uploading || photo.metadataLoading}>
                                <option value="">{t("photoBulk.notAssociated")}</option>
                                {stages.map(stage => <option key={stage.key} value={stage.key} disabled={assignedKeys.includes(stage.key) && stage.key !== photo.stageKey}>{stage.label}{stage.event.image ? ` · ${t("photoBulk.replacesExisting")}` : ""}</option>)}
                              </select>
                              {selectedStage?.event.image && <div className="text-xs couleurRose mt-1">{t("photoBulk.replaceWarning")}</div>}
                            </>
                          )}
                        </div>
                        <button type="button" disabled={uploading} className="text-sm text-red-600 self-start" onClick={() => removePhoto(photo.id)}>{t("photoBulk.remove")}</button>
                      </div>
                    );
                  })}
                </div>
                )}
                {!preparing && <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mt-6 pt-4 border-t border-neutral-200 dark:border-neutral-700">
                  <div className="text-sm">{uploading ? t("photoBulk.progress", { current: progress, total: assignedPhotos.length }) : t("photoBulk.selectedCount", { count: assignedPhotos.length })}</div>
                  <div className="flex gap-3 justify-end">
                    <ButtonSecondary onClick={onClose} disabled={uploading}>{t("photoBulk.annuler")}</ButtonSecondary>
                    <ButtonPrimary onClick={upload} disabled={preparing || !assignedPhotos.length || uploading || metadataPending}>{uploading ? t("photoBulk.uploading") : t("photoBulk.validate")}</ButtonPrimary>
                  </div>
                </div>}
              </div>
            </div>
          </Transition.Child>
        </div>
      </Dialog>
    </Transition>
  );
};

export default ModalBulkPhotoUpload;
